/**
 * Switch vs Stay Calculator capture form — /switch-vs-stay, shown under the
 * result. Replaces the old inline LeadForm and the "email me the results"
 * pill.
 *
 * The calculator passes its inputs and result as `context` (see
 * SwitchVsStayCalculator.jsx → buildLeadContext). Keys read here:
 *   category, monthly_saving_gbp, break_even_months, contract_status (a
 *   CONTRACT_STATUS_OPTIONS label), left_at_default (labels of inputs the
 *   visitor never changed).
 * The calculator shows results straight away from pre-filled example
 * figures, so Contract_Status is only set once the visitor has chosen one.
 * Everything in context also lands in the Lead's Note.
 */
import { CONTRACT_STATUS_OPTIONS, company, currentProvider, email, fullName, phone } from "./common";

const has = (v) => typeof v === "number" && Number.isFinite(v);

/** Label used in `left_at_default` for the contract question. */
export const CONTRACT_STATUS_INPUT_LABEL = "Contract status";

const contractStatusCrm = (label) => CONTRACT_STATUS_OPTIONS.find((o) => o.label === label)?.crm;

const toolSwitchVsStay = {
  id: "tool_switch_vs_stay",
  label: "Switch vs Stay Calculator",
  crm: { layout: "Standard", leadSource: "Website Calculator - Switch vs Stay" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
    currentProvider,
  ],
  submitLabel: "Get my free review",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We've got your figures and details. We'll be in touch with your personalised review shortly.",
  },
  contextToCrm: (ctx) => ({
    Contract_Status: (ctx.left_at_default || []).includes(CONTRACT_STATUS_INPUT_LABEL)
      ? undefined
      : contractStatusCrm(ctx.contract_status),
  }),
  summary: (values, ctx) => {
    if (!has(ctx.monthly_saving_gbp)) return "Switch vs Stay Calculator";
    const saving = ctx.monthly_saving_gbp;
    const parts = [
      saving > 0
        ? `Switch vs Stay: £${saving.toFixed(2)}/mo saving`
        : `Switch vs Stay: no monthly saving${saving < 0 ? ` (£${Math.abs(saving).toFixed(2)}/mo more)` : ""}`,
    ];
    if (has(ctx.break_even_months)) {
      parts.push(ctx.break_even_months > 0 ? `break-even ${ctx.break_even_months} months` : "no switching costs");
    }
    // "No immediate saving" would just repeat the first part.
    const category = ctx.category && saving > 0 ? ` (${ctx.category.toLowerCase()})` : "";
    return `${parts.join(", ")}${category}`;
  },
};

export default toolSwitchVsStay;
